import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { zodResolver } from '@hookform/resolvers/zod';
import { useForm } from 'react-hook-form';
import { z } from 'zod';
import { useState } from 'react';
import { Puzzle, Sparkles } from 'lucide-react';
import {
  apiClient,
  type ApiError,
  type CustomizationRequest,
  type CustomizationRequestStatus,
} from '@/lib/api-client';
import { Button } from '@/components/ui/button';
import { Field, TextArea, TextInput } from '@/components/clients/form-primitives';
import { ConfigModal } from './config-modal';

/**
 * Customizacoes — secao nova das Configuracoes do escritorio.
 *
 * Duas partes:
 *  1. "Customizacoes ativas": por ora sempre vazia (nao ha campo de
 *     customizacao ativa nesta rodada, so o pedido). Empty state fixo.
 *  2. "Solicitacoes": lista das solicitacoes do tenant + formulario para
 *     abrir uma nova. A plataforma orca e acompanha por fora; admin_notes
 *     e a unica resposta que aparece aqui.
 */

const STATUS_LABEL: Record<CustomizationRequestStatus, string> = {
  pending: 'Pendente',
  reviewing: 'Em análise',
  quoted: 'Orçada',
  in_progress: 'Em desenvolvimento',
  done: 'Concluída',
  rejected: 'Recusada',
};

const STATUS_BADGE_CLASS: Record<CustomizationRequestStatus, string> = {
  pending: 'bg-zinc-500/15 text-zinc-400',
  reviewing: 'bg-amber-500/15 text-amber-500',
  quoted: 'bg-sky-500/15 text-sky-500',
  in_progress: 'bg-violet-500/15 text-violet-500',
  done: 'bg-emerald-500/15 text-emerald-500',
  rejected: 'bg-red-500/15 text-red-500',
};

function getErrorMessage(err: unknown): string {
  const apiErr = err as ApiError | undefined;
  if (apiErr?.message) return apiErr.message;
  if (err instanceof Error && err.message) return err.message;
  return 'Erro inesperado. Tente novamente.';
}

const requestFormSchema = z.object({
  title: z
    .string()
    .trim()
    .min(1, 'Título obrigatório')
    .max(200, 'Título muito longo (máx. 200)'),
  description: z
    .string()
    .trim()
    .min(1, 'Descrição obrigatória')
    .max(5000, 'Descrição muito longa (máx. 5000)'),
});

type RequestFormValues = z.infer<typeof requestFormSchema>;

const EMPTY_VALUES: RequestFormValues = { title: '', description: '' };

export function CustomizationsPage() {
  const queryClient = useQueryClient();
  const [modalOpen, setModalOpen] = useState(false);

  const listQ = useQuery({
    queryKey: ['customizations', 'requests'],
    queryFn: () => apiClient.customizationRequestsList(),
  });

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<RequestFormValues>({
    resolver: zodResolver(requestFormSchema),
    defaultValues: EMPTY_VALUES,
  });

  const createMut = useMutation({
    mutationFn: (values: RequestFormValues) =>
      apiClient.customizationRequestsCreate(values),
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: ['customizations', 'requests'],
      });
      setModalOpen(false);
      reset(EMPTY_VALUES);
    },
  });

  function openNew() {
    reset(EMPTY_VALUES);
    setModalOpen(true);
  }

  function onSubmit(values: RequestFormValues) {
    createMut.mutate(values);
  }

  const requests = listQ.data ?? [];

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-xl font-semibold tracking-tight text-[var(--fg)] md:text-2xl">
          Customizações
        </h1>
        <p className="mt-1 text-sm text-[var(--fg-muted)]">
          Precisa de algo sob medida para o seu escritório? Solicite abaixo,
          a plataforma orça e acompanha por fora.
        </p>
      </div>

      <section className="space-y-3">
        <h2 className="text-sm font-semibold text-[var(--fg)]">
          Customizações ativas
        </h2>
        <div className="overflow-hidden rounded-lg border border-[var(--border)] bg-[var(--surface)]">
          <div className="flex flex-col items-center gap-3 px-4 py-12 text-center">
            <Puzzle className="h-10 w-10 text-[var(--fg-muted)] opacity-40" />
            <p className="text-sm text-[var(--fg-muted)]">
              Nenhuma customização ativa.
            </p>
            <p className="max-w-sm text-xs text-[var(--fg-muted)] opacity-70">
              Customizações sob medida (integrações, campos extras, fluxos
              específicos) podem ser solicitadas na seção abaixo.
            </p>
          </div>
        </div>
      </section>

      <section className="space-y-3">
        <div className="flex flex-col gap-1 md:flex-row md:items-end md:justify-between">
          <h2 className="text-sm font-semibold text-[var(--fg)]">Solicitações</h2>
          <Button onClick={openNew}>
            <Sparkles className="mr-2 h-4 w-4" />
            Solicitar customização
          </Button>
        </div>

        <div className="overflow-hidden rounded-lg border border-[var(--border)] bg-[var(--surface)]">
          {listQ.isPending ? (
            <p className="px-4 py-8 text-center text-sm text-[var(--fg-muted)]">
              Carregando...
            </p>
          ) : listQ.isError ? (
            <p className="px-4 py-8 text-center text-sm text-red-500">
              {getErrorMessage(listQ.error)}
            </p>
          ) : requests.length === 0 ? (
            <div className="flex flex-col items-center gap-3 px-4 py-12 text-center">
              <Sparkles className="h-10 w-10 text-[var(--fg-muted)] opacity-40" />
              <p className="text-sm text-[var(--fg-muted)]">
                Nenhuma solicitação registrada ainda.
              </p>
            </div>
          ) : (
            <table className="w-full text-sm">
              <thead className="bg-[color-mix(in_oklch,var(--fg)_4%,transparent)] text-left text-xs uppercase tracking-wide text-[var(--fg-muted)]">
                <tr>
                  <th className="px-4 py-2 font-medium">Título</th>
                  <th className="px-4 py-2 font-medium">Data</th>
                  <th className="px-4 py-2 font-medium">Status</th>
                  <th className="px-4 py-2 font-medium">Observação da plataforma</th>
                </tr>
              </thead>
              <tbody>
                {requests.map((request: CustomizationRequest) => (
                  <tr
                    key={request.id}
                    className="border-t border-[var(--border)]"
                  >
                    <td className="px-4 py-2">
                      <p className="font-medium text-[var(--fg)]">
                        {request.title}
                      </p>
                      <p className="mt-0.5 line-clamp-2 text-xs text-[var(--fg-muted)]">
                        {request.description}
                      </p>
                    </td>
                    <td className="px-4 py-2 text-xs text-[var(--fg-muted)]">
                      {new Date(request.createdAt).toLocaleDateString('pt-BR')}
                    </td>
                    <td className="px-4 py-2">
                      <span
                        className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium ${STATUS_BADGE_CLASS[request.status]}`}
                      >
                        {STATUS_LABEL[request.status]}
                      </span>
                    </td>
                    <td className="px-4 py-2 text-xs text-[var(--fg-muted)]">
                      {request.adminNotes ? request.adminNotes : '·'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </section>

      <ConfigModal
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        title="Solicitar customização"
        description="Descreva o que precisa. A plataforma responde com o orçamento por fora."
        footer={
          <>
            <Button
              variant="outline"
              onClick={() => setModalOpen(false)}
              disabled={createMut.isPending}
            >
              Cancelar
            </Button>
            <Button
              onClick={handleSubmit(onSubmit)}
              disabled={createMut.isPending}
            >
              {createMut.isPending ? 'Enviando...' : 'Enviar solicitação'}
            </Button>
          </>
        }
      >
        <form onSubmit={handleSubmit(onSubmit)} className="flex flex-col gap-4">
          {createMut.isError && (
            <p className="rounded-md bg-red-500/10 px-3 py-2 text-xs text-red-500">
              {getErrorMessage(createMut.error)}
            </p>
          )}
          <Field label="Título" required error={errors.title?.message}>
            <TextInput
              {...register('title')}
              placeholder="Ex: Integração com sistema de terceiro"
              maxLength={200}
            />
          </Field>
          <Field
            label="Descrição"
            required
            error={errors.description?.message}
            hint="Quanto mais detalhe, mais rápido o orçamento."
          >
            <TextArea
              {...register('description')}
              rows={5}
              placeholder="Descreva o que precisa, contexto e resultado esperado."
              maxLength={5000}
            />
          </Field>
        </form>
      </ConfigModal>
    </div>
  );
}
